import { motion } from 'framer-motion';
import { useMemo, memo, useCallback, type ChangeEvent as ReactChangeEvent } from 'react';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  Box,
  ClickAwayListener,
  Column,
  Divider,
  Input,
  ScrollArea,
} from '~/commonComponents';
import { useAppSelector, useToggle } from '~/hooks';
import ArrowDownIcon from '~/images/icons/ArrowDown';
import SearchIcon from '~/images/search-icon.svg';
import { Animations, Styles } from './Constants';
import { IInputSearchOfficeProps, IOfficeOption } from './Types';
import { useOfficeSearch } from './useOfficeSearch';

import './Styles.scss';

const InputSearchOfficeComponent = ({
  placeholder = 'Select Office',
  onChange,
  value,
}: IInputSearchOfficeProps) => {
  const [isShowOptions, controls] = useToggle();

  const offices = useAppSelector((state) => state.user.childOffices || []);

  const options = useMemo(
    () =>
      offices.map((it) => ({
        label: it.office_name,
        value: it.id,
      })) as IOfficeOption[],
    [offices],
  );

  const selectedOffice = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );

  const handleOfficeChange = useCallback(
    (office: IOfficeOption) => onChange?.(office.value),
    [onChange],
  );

  const {
    searchQuery,
    optimisticSelection,
    filteredOptions,
    inputRef,
    handleOfficeSelect,
    handleSearchChange,
  } = useOfficeSearch({
    options,
    selectedOffice,
    onOfficeSelect: handleOfficeChange,
    onCloseOptions: controls.setOff,
  });

  const handleInputChange = (e: ReactChangeEvent<HTMLInputElement>) => {
    handleSearchChange(e.target.value);
  };

  return (
    <Column position="relative">
      <button style={Styles.Trigger} type="button" onClick={controls.setOn}>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <Typography
            as="span"
            color={optimisticSelection ? colors.neutral[700] : colors.neutral[300]}
            textAlign="left"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'normal',
            }}
          >
            {optimisticSelection ? optimisticSelection.label : placeholder}
          </Typography>
        </div>
        <ArrowDownIcon />
      </button>
      <ClickAwayListener onClickAway={controls.setOff}>
        <motion.div
          animate={isShowOptions ? 'show' : 'hide'}
          initial="hide"
          style={Styles.Popover}
          transition={Animations.Popover.transition}
          variants={Animations.Popover.variants}
        >
          <Column paddingInline={24} paddingTop={10} paddingBottom={10} gap={10}>
            <Input
              ref={inputRef}
              PrefixComponent={
                <img
                  src={SearchIcon}
                  style={{
                    width: 24,
                    height: 24,
                  }}
                  alt="search_icon"
                />
              }
              placeholder="Search Office"
              value={searchQuery}
              onChange={handleInputChange}
            />
          </Column>
          <Divider color={colors.neutral[200]} height={1} marginVertical={0} />
          <ScrollArea style={Styles.ScrollArea}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((office) => (
                <Box
                  className="options-item"
                  justifyContent="center"
                  key={office.value}
                  paddingInline={16}
                  paddingVertical={6}
                  onClick={() => handleOfficeSelect(office)}
                >
                  <Typography
                    as="span"
                    style={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      whiteSpace: 'normal',
                    }}
                  >
                    {office.label}
                  </Typography>
                </Box>
              ))
            ) : (
              <Box justifyContent="center" paddingInline={16}>
                <Typography as="span" color={colors.neutral[400]} style={{ textWrap: 'wrap' }}>
                  Office not found. Please make sure the name is spelled correctly.
                </Typography>
              </Box>
            )}
          </ScrollArea>
        </motion.div>
      </ClickAwayListener>
    </Column>
  );
};

export const InputSearchOffice = memo(InputSearchOfficeComponent);
